
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { ConversationContext } from '@/types/insurance';

export interface UseChatContextDataResult {
  context: ConversationContext | null;
  loadContext: () => Promise<void>;
  updateContext: (updates: Partial<ConversationContext>) => Promise<void>;
  threadId: string | null;
  useOpenAI: boolean;
  toggleModel: () => void;
}

export const useChatContextData = (chatId?: string): UseChatContextDataResult => {
  const [context, setContext] = useState<ConversationContext | null>(null);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [isUsingOpenAI, setIsUsingOpenAI] = useState<boolean>(true);
  const { user } = useAuth();
  
  const loadContext = async () => {
    if (!chatId || !user) return;
    
    try {
      const { data, error } = await supabase
        .from('conversation_contexts')
        .select('*')
        .eq('chat_id', chatId)
        .single();
        
      if (error && error.code !== 'PGRST116') {
        console.error('Error loading context:', error);
        return;
      }
      
      if (data) {
        // Normalize the data to include both snake_case and camelCase
        const normalizedData: ConversationContext = {
          ...data,
          userId: data.user_id,
          chatId: data.chat_id,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
          contextType: data.context_data && typeof data.context_data === 'object' ? 
            (data.context_data as any).context_type : undefined, // Updated to use context_data
          contextData: data.context_data // Updated to match DB structure
        };
        
        setContext(normalizedData);
        
        if (data.context_data && typeof data.context_data === 'object' && 
            (data.context_data as any).thread_id) {
          setThreadId((data.context_data as any).thread_id);
        }
      } else {
        // Create a new context
        await createContext();
      }
    } catch (error) {
      console.error('Error loading context:', error);
    }
  };
  
  const createContext = async () => {
    if (!chatId || !user) return;
    
    try {
      const { data, error } = await supabase
        .from('conversation_contexts')
        .insert({
          chat_id: chatId,
          user_id: user.id,
          context_data: { context_type: 'chat' } // Updated to use context_data
        })
        .select()
        .single();
        
      if (error) {
        throw error;
      }
      
      if (data) {
        // Normalize the data to include both snake_case and camelCase
        const normalizedData: ConversationContext = {
          ...data,
          userId: data.user_id,
          chatId: data.chat_id,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
          contextType: data.context_data && typeof data.context_data === 'object' ? 
            (data.context_data as any).context_type : undefined, // Updated to use context_data
          contextData: data.context_data
        };
        
        setContext(normalizedData);
      }
    } catch (error) {
      console.error('Error creating context:', error);
    }
  };
  
  const updateContext = async (updates: Partial<ConversationContext>) => {
    if (!context || !chatId) return;
    
    try {
      // Convert camelCase to snake_case for DB operations
      const dbUpdates: any = {};
      
      // For conversation_contexts table, we need to update the context_data field
      if (updates.contextData) {
        dbUpdates.context_data = updates.contextData;
      } else {
        // If we're not completely replacing context_data, update parts of it
        const updatedContextData = { 
          ...(context.contextData || {}), 
        };
        
        // Update AI model if specified
        if ('ai_model' in updates && updates.ai_model) {
          updatedContextData.ai_model = !isUsingOpenAI ? 'openai' : 'anthropic';
        }
        
        dbUpdates.context_data = updatedContextData;
      }
      
      // Add other direct properties if needed
      if ('userId' in updates) {
        dbUpdates.user_id = updates.userId;
      }
      
      const { error } = await supabase
        .from('conversation_contexts')
        .update(dbUpdates)
        .eq('chat_id', chatId);
        
      if (error) {
        throw error;
      }
      
      // Update the local state
      setContext(prev => prev ? { ...prev, ...updates } : null);
    } catch (error) {
      console.error('Error updating context:', error);
    }
  };
  
  const toggleModel = () => {
    setIsUsingOpenAI(prev => !prev);
    
    // Create updatedContextData with ai_model field
    const updatedContextData = {
      ...(context?.contextData || {}),
      ai_model: !isUsingOpenAI ? 'openai' : 'anthropic'
    };
    
    // Update context with the modified contextData
    updateContext({ contextData: updatedContextData });
  };
  
  useEffect(() => {
    if (chatId && user) {
      loadContext();
    }
  }, [chatId, user]);
  
  return {
    context,
    loadContext,
    updateContext,
    threadId,
    useOpenAI: isUsingOpenAI,
    toggleModel
  };
};
